import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { GeneralParameters } from 'src/app/constants/general-parameters-constans';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { UnitModel } from 'src/app/models/archive/unit.model';
import { UnitService } from 'src/app/services/archive/unit.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-unit',
  templateUrl: './register-unit.component.html',
  styleUrls: ['./register-unit.component.scss'],
  standalone: false
})
export class RegisterUnitComponent implements OnInit {

  disabledForm: boolean = false;
  formUnit: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Unidades de Medidas';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  swMaintenanceOrder: boolean = false;
  swQuotation: boolean = false;
  url: string = 'unit';
  unitContract: UnitModel = new UnitModel();
  unitId: number = 0;

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private unitService: UnitService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.validateForm(new UnitModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.unitId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar unidades de medidas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.unitId || this.unitId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        `Debe crear una unidad de medida.`);
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la unidad de medida?')) {
      try {
        await firstValueFrom(this.unitService.deleteUnit(this.unitContract.unitId));
        this.createNewRegister();
        this.toastHandlerService.showToast(
          'success',
          'La unidad de medida fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la unidad de medida.', error);
      }
    }
  }

  changeUnit(code: string) {
    if (code != "") {
      this.getUnitByCode(code);
    } else {
      this.createNewRegister();
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear unidades de medidas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.unitId = 0;
    this.unitContract = new UnitModel();
    this.validateForm(this.unitContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  getGeneralParametersByCompany() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.generalParameterObservable.subscribe(response => {
        this.swQuotation = response.some(x => x.name === GeneralParameters.quotationsModule);
        this.swMaintenanceOrder = response.some(x => x.name === GeneralParameters.maintenanceOrder);
      });
      resolve(true);
    });
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
    this.getGeneralParametersByCompany();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getUnitById() {
    try {
      const response = await firstValueFrom(this.unitService.getUnitById(this.unitId));
      this.unitContract = response;
      this.validateForm(this.unitContract);
    } catch (error) {
      console.error('Error al obtener la unidad de medida por id.', error);
    }
  }

  async getUnitByCode(code: string) {
    try {
      const response = await firstValueFrom(this.unitService.getUnitByCode(code));
      this.unitId = response.unitId;
      this.unitContract = response;
      this.validateForm(this.unitContract);
      this.location.replaceState(`/${this.url}/register/${this.unitId}`);
    } catch (error) {
      console.error('Error al obtener la unidad de medida por codigo.', error);
    }
  }

  async handleInitialState() {
    if (this.unitId !== null && this.unitId !== 0) {
      await this.getUnitById();
    } else {
      this.createNewRegister();
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formUnit.valid) {
      const modelUnit = this.formUnit.getRawValue() as UnitModel;
      if (this.unitId == 0) {
        try {
          const response = await firstValueFrom(this.unitService.createUnit(modelUnit));
          this.unitId = response.unitId;
          this.unitContract = response;
          this.validateForm(this.unitContract);
          this.location.replaceState(`/${this.url}/register/${this.unitId}`);
          this.toastHandlerService.showToast(
            'success',
            `La unidad de medida fue creada exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear la unidad de medida.', error);
        }
      } else {
        this.unitContract.code = modelUnit.code;
        this.unitContract.name = modelUnit.name;
        this.unitContract.isTypeInventory = modelUnit.isTypeInventory;
        this.unitContract.isTypeRental = modelUnit.isTypeRental;
        this.unitContract.isTypeQuotation = modelUnit.isTypeQuotation;
        this.unitContract.isTypeMaintenance = modelUnit.isTypeMaintenance;
        try {
          const response = await firstValueFrom(this.unitService.updateUnit(this.unitContract));
          this.unitContract = response;
          this.validateForm(this.unitContract);
          this.toastHandlerService.showToast(
            'success',
            `La unidad de medida fue modificada exitosamente.`
          );
        } catch (error) {
          console.error('Error al actualizar la unidad de medida.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.',
        maxlength: 'El Código no puede tener más de 4 caracteres.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 50 caracteres.'
      },
      isTypeInventory: {
        required: 'Debe indicar si la unidad es de tipo Inventario.'
      },
      isTypeRental: {
        required: 'Debe indicar si la unidad es de tipo Alquiler.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formUnit.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formUnit.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formUnit.disable());
    }
  }

  validateForm(unit: UnitModel) {
    this.formUnit = this.formBuilder.group({
      code: [unit.code, [Validators.required, Validators.maxLength(4)]],
      name: [unit.name, [Validators.required, Validators.maxLength(50)]],
      isTypeInventory: [unit.isTypeInventory],
      isTypeRental: [unit.isTypeRental],
      isTypeQuotation: [unit.isTypeQuotation],
      isTypeMaintenance: [unit.isTypeMaintenance]
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formUnit.controls).forEach((key) => {
      const control = this.formUnit.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
